/**
 * CounterSales — R7a (2026-05-18) — PII-bearing report.
 *
 * Row-level counter sales: vouchers tied to *subscription-type*
 * organization CTAs (no event context). Per 2026-05-18 operator
 * clarification, subscription CTAs only emit vouchers when the
 * prospect actually subscribes, so these rows are confirmed
 * subscription conversions credited to the rep tagged on the CTA.
 * Route gated by RequireRole (admin/manager); RPC enforces
 * server-side. Mirrors VerifiedMembers (R5) shape.
 *
 * NOTE 1: counter_sales_v currently reads octa.sales_position_id
 * directly. When Option B Phase 4 ships (voucher-rep snapshot), the
 * view will be rewritten to read vouchers.credited_rep_position_id
 * so historical credit becomes immutable per voucher. The UI shape
 * here is stable; the underlying source is what changes.
 *
 * NOTE 2: the "CTA Discount" column shows the CTA's configured
 * promotional discount amount, NOT the customer's subscription sale
 * value. The latter lives in Zuora webhook data and is queued as a
 * follow-up (see task: "Wire Zuora subscription value into Counter
 * Sales (post-R7a)"). Don't conflate the two.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCounterSalesReport,
  type CounterSalesReportRow,
  type SalesActivityReportFilter,
} from "../../services/salesActivities.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
  logPiiExport,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import { LockClosedIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const formatCurrency = (amount: number | string | null): string => {
  if (amount === null || amount === undefined || amount === "") {
    return "";
  }
  // supabase-js may serialize NUMERIC columns as strings; coerce so
  // toLocaleString's currency options actually apply.
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) {
    return "";
  }
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): SalesActivityReportFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  repPositionId: f.repPositionId,
  page,
  limit,
});

const CounterSales: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.orgId, filters.repPositionId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-counter-sales", filters, page],
    queryFn: () => getCounterSalesReport(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: CounterSalesReportRow) => r.credited_rep_position_id)
            .filter((id: string | null): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["counter-sales-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "Unattributed";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Voucher ID",
    "Voucher Code",
    "Created",
    "Credited Rep",
    "Contact First Name",
    "Contact Last Name",
    "Contact Email",
    "Contact Phone",
    "Member Number",
    "Organization ID",
    "CTA Discount",
    "Subscription Revenue",
    "Voucher Status",
  ];

  const mapRows = async (
    pageRows: CounterSalesReportRow[],
  ): Promise<Record<string, unknown>[]> => {
    const ids = Array.from(
      new Set(
        pageRows
          .map((r) => r.credited_rep_position_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const positionRowsExport = await resolvePositionNamesBulk(ids);
    const exportPositionsById = indexPositionsById(positionRowsExport);
    const exportRepDisplay = (positionId: string | null) => {
      if (!positionId) {
        return "Unattributed";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };

    // R5-D5 PII contract: log the export BEFORE returning rows. If the
    // audit log throws, ExportButton's try/catch surfaces a toast and
    // the file does not download.
    await logPiiExport(
      "counter_sales",
      pageRows.length,
      filters as unknown as Record<string, unknown>,
    );

    return pageRows.map((r) => ({
      "Voucher ID": r.voucher_id,
      "Voucher Code": r.voucher_code,
      Created: formatDate(r.voucher_created_at),
      "Credited Rep": exportRepDisplay(r.credited_rep_position_id),
      "Contact First Name": r.contact_first_name ?? "",
      "Contact Last Name": r.contact_last_name ?? "",
      "Contact Email": r.contact_email ?? "",
      "Contact Phone": r.contact_phone ?? "",
      "Member Number": r.member_number ?? "",
      "Organization ID": r.organization_id ?? "",
      "CTA Discount": r.voucher_amount ?? "",
      "Subscription Revenue":
        r.subscription_revenue === null
          ? "Not available"
          : r.subscription_revenue,
      "Voucher Status": r.voucher_status ?? "",
    }));
  };

  const fetchPage = async (f: ReportFilterState, p: number, l: number) => {
    const res = await getCounterSalesReport(filterToRpc(f, p, l));
    return { rows: res.rows, total: res.total };
  };

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Counter Sales"
        description="Subscription-CTA voucher confirmations (no event context). Subscription Revenue is the member's actual Zuora subscription value; it reads 'Not available' until that member's Zuora data has synced. CTA Discount is the CTA's configured promotional discount (kept for reference)."
        badges={
          <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
            <LockClosedIcon className="h-3 w-3" />
            PII
          </span>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={fetchPage}
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="counter-sales"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showRep={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Voucher</th>
              <th className="px-3 py-2">Credited Rep</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Member #</th>
              <th className="px-3 py-2 text-right">CTA Discount</th>
              <th className="px-3 py-2 text-right">Subscription Revenue</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  No counter sales match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r: CounterSalesReportRow) => (
              <tr
                key={r.voucher_id}
                className={`hover:bg-gray-50 ${
                  !r.credited_rep_position_id ? "bg-yellow-50" : ""
                }`}
              >
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDate(r.voucher_created_at)}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.voucher_code}
                </td>
                <td className="px-3 py-2">
                  {repDisplay(r.credited_rep_position_id)}
                </td>
                <td className="px-3 py-2">
                  {[r.contact_first_name, r.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </td>
                <td className="px-3 py-2">{r.contact_email || "—"}</td>
                <td className="px-3 py-2">{r.member_number || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {formatCurrency(r.voucher_amount)}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.subscription_revenue === null ? (
                    <span className="text-gray-400">Not available</span>
                  ) : (
                    formatCurrency(r.subscription_revenue)
                  )}
                </td>
                <td className="px-3 py-2">{r.voucher_status || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CounterSales;
